<script setup lang="ts">
  import { useHttpClient, useAuthenticated, useRoute } from '@othercommerce/foundation';
  import { useBreakpoints } from '@storefront/storefront';
  import type { ProductReviewResource } from '@storefront/resources';

  interface Props {
    review: ProductReviewResource;
  }

  const props = defineProps<Props>();
  const route = useRoute();
  const breakpoints = useBreakpoints();
  const session = useAuthenticated();
  const http = useHttpClient();
  const emit = defineEmits(['refreshComments']);

  function onVoteClick() {
    if (props.review.isLiked) {
      http.delete(route('api.v1.web.review.vote.destroy', { review: props.review })).then(() => {
        emit('refreshComments');
      });
    } else {
      http.post(route('api.v1.web.review.vote.store', { review: props.review })).then(() => {
        emit('refreshComments');
      });
    }
  }
</script>
<template>
  <div class="review-comment__opinion">
    <div class="review-comment__opinion-header">
      <div class="review-comment__opinion-header-wrap">
        <p class="review-comment__opinion-name">{{ review.name }}</p>
        <div class="review-comment__opinion-header-info">
          <span class="review-comment__opinion-rating">{{ review.rating.toFixed(1) }}</span>
          <SfStars :rating="review.rating" />
          <span class="review-comment__opinion-date">
            {{ review.createdAt }}
          </span>
        </div>
      </div>

      <SfButton
        :disabled="!session"
        :title="session ? $t('SHOP.PRODUCT.ACTION.REVIEW_LIKE') : $t('SHOP.PRODUCT.MESSAGE.ONLY_AUTHENTICATED_USER_CAN_LIKE_REVIEW')"
        class="review-comment__opinion-vote"
        transparent
        :small="!breakpoints.sm"
        @click="onVoteClick()"
      >
        <template #default>
          {{ review.upvotes }}
        </template>
        <template #after>
          <SfIconLike :class="{ 'review-comment__opinion-thumb--active': review.isLiked }" class="review-comment__opinion-thumb" aria-hidden="true" />
        </template>
      </SfButton>
    </div>
    <p class="review-comment__review">
      {{ review.comment }}
    </p>
  </div>
</template>
<style lang="scss">
  @use '@storefront/styles/mixins/media';

  :root {
    --shop-product-review-comments-padding: 0.375rem 0.5rem;
    --shop-product-review-comments-background: #fff;
    --shop-product-review-comments-border-radius: var(--border-radius);
    --shop-product-review-comments-border-top: none;
    --shop-product-review-comments-border-right: none;
    --shop-product-review-comments-border-bottom: 1px solid var(--secondary-border);
    --shop-product-review-comments-border-left: none;

    --shop-product-review-comments-name-font-size: 1.125rem;

    @include media.query(md) {
      --shop-product-review-comments-border-radius: 0;
      --shop-product-review-comments-name-font-size: 0.875rem;
    }
  }
</style>
<style scoped lang="scss">
  @use '@storefront/styles/mixins/media';

  .review-comment {
    &__opinion {
      padding: var(--shop-product-review-comments-padding);
      background: var(--shop-product-review-comments-background);
      border-radius: var(--shop-product-review-comments-border-radius);
      border-top: var(--shop-product-review-comments-border-top);
      border-right: var(--shop-product-review-comments-border-right);
      border-bottom: var(--shop-product-review-comments-border-bottom);
      border-left: var(--shop-product-review-comments-border-left);
    }

    &__opinion-header {
      --sf-stars-padding-y: 0;
      --sf-stars-star-size: 0.875rem;
      --sf-stars-gap: 0.25rem;

      justify-content: space-between;
      gap: 1.5rem;
    }

    &__opinion-header-info {
      gap: 0.5rem;
    }

    &__opinion-header-wrap,
    &__opinion,
    &__opinion-header-info,
    &__opinion-header,
    &__opinion-date {
      display: flex;
    }

    &__opinion-header-wrap,
    &__opinion {
      flex-direction: column;
    }

    &__opinion-header-wrap {
      gap: 0.125rem;
    }

    &__opinion {
      gap: 0.25rem
    }

    &__opinion-name {
      font-weight: var(--font-weight-bold);
      font-size: var(--shop-product-review-comments-name-font-size)
    }

    &__opinion-vote {
      font-weight: var(--font-weight-semi);
    }

    &__opinion-date {
      align-items: center;
      font-size: 0.875rem;
    }

    &__review {
      font-size: 0.875rem;
    }

    &__opinion-thumb {
      font-size: 1rem;

      &--active {
        color: var(--primary-color);
      }
    }
  }

  @include media.query(md) {
    .review-comment {

      &__opinion-header {
        column-gap: 1rem;
        margin-bottom: 0;
        font-size: 1.125rem;
      }

      &__opinion-name {
        font-size: 0.875rem;
      }

      &__opinion-rating {
        font-size: 0.875rem;
      }
    }
  }
</style>
